import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { IdbStorage } from "@/store/idbStorage.ts";
import { CreditData, PaymentDate } from "@/models/sale.model.ts";
import {
  DEFAULT_ADDITIONAL_DATA,
  IAdditionalData,
} from "@/pages/point-of-sale/models/addition-data.model.ts";
import { CancelSale, ISale } from "@/pages/point-of-sale/models/sale.model.ts";
import {
  CreditNote,
  SalesResponse,
  SalesWithCreditNotes,
  Refund,
} from "@/pages/[others]/pages/sales/models/sale.interface";
import { ISaleAddResponseModel } from "@/pages/point-of-sale/models/sale-add-response.model.ts";
import { groupItemsByProductAndContainer } from "@/utilities/groupedItems";
import { v4 as uuidv4 } from "uuid";
import {
  isStoreSaleSyncExhausted,
  MAX_OFFLINE_SYNC_ATTEMPTS,
} from "@/utilities/offline-sync-retry.constants";
import type { OpenSaleSessionSnapshot } from "@/utilities/offline-sale-session-reassign.utils";

export interface ISalesState {
  taxedAmount: number;
  setTaxedAmount: (taxedAmount: number) => void;
  exemptedAmount: number;
  setExemptedAmount: (exemptedAmount: number) => void;
  unaffectedAmount: number;
  setUnaffectedAmount: (unaffectedAmount: number) => void;
  freeAmount: number;
  setFreeAmount: (freeAmount: number) => void;
  vatAmount: number;
  setVatAmount: (vatAmount: number) => void;
  grossAmount: number;
  setGrossAmount: (grossAmount: number) => void;
  totalAmount: number;
  setTotalAmount: (totalAmount: number) => void;
  discount: number;
  setDiscount: (discount: number) => void;
  discountType: string;
  setDiscountType: (discountType: string) => void;
  creditData: CreditData | undefined;
  setCreditData: (creditData: CreditData | undefined) => void;
  paymentDates: PaymentDate[];
  setPaymentDates: (paymentDates: PaymentDate[]) => void;
  additionalData: IAdditionalData;
  setAdditionalData: (additionalData: IAdditionalData) => void;
  sales: ISale[];
  addSale: (sale: ISale) => void;
  listSale: SalesResponse[];
  setListSale: (listSale: SalesResponse[]) => void;
  lastSaleUuid: string | undefined;
  setLastSaleUuid: (lastSaleUuid: string | undefined) => void;
  filterPaymentMethod: number;
  setFilterPaymentMethod: (filterPaymentMethod: number) => void;
  filterBillType: number;
  setFilterBillType: (filterBillType: number) => void;
  startDate: Date | undefined;
  setStartDate: (startDate: Date | undefined) => void;
  endDate: Date | undefined;
  setEndDate: (endDate: Date | undefined) => void;
  offline: boolean;
  setOffline: (offline: boolean) => void;
  cancelSaleItem: CancelSale[];
  setCancelSaleItem: (cancelSale: CancelSale[]) => void;
  getSale: (uuid: string) => ISale | undefined;
  updateSaleWithResponse: (
    uuid: string,
    saleResponse: ISaleAddResponseModel
  ) => void;
  enableQuickPay: boolean;
  setEnableQuickPay: (enableQuickPay: boolean) => void;
  success: boolean;
  setSuccess: (success: boolean) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  allLocalSales: SalesWithCreditNotes;
  setAllLocalSales: (sales: SalesWithCreditNotes) => void;
  /** Merge inteligente: combina datos del servidor con ventas locales no sincronizadas */
  mergeWithServerData: (serverData: SalesWithCreditNotes) => void;
  addLocalSaleRecord: (record: SalesResponse | CreditNote) => void;
  /** Agrega refund local para visualización temporal (no se sincroniza, el servidor lo genera al procesar anulación) */
  addLocalRefund: (refund: Refund) => void;
  /** Obtiene ventas que aún no han sido sincronizadas con el servidor */
  getUnsyncedSales: () => SalesResponse[];
  /** Ventas pendientes elegibles para sincronización automática (sin agotar reintentos) */
  getUnsyncedSalesForAutoSync: () => SalesResponse[];
  recordStoreSaleSyncFailure: (offlineUuid: string, errorMessage: string) => void;
  /** Bloquea reintentos automáticos (caja cerrada / caja distinta) sin redirigir a /stores. */
  markStoreSaleSyncBlocked: (offlineUuid: string, errorMessage: string) => void;
  resetStoreSaleSyncAttempts: (offlineUuid: string) => void;
  /** Actualiza caja, turno y vendedor en la venta local tras confirmar reenvío manual. */
  updateOfflineSaleOpenSession: (
    offlineUuid: string,
    session: OpenSaleSessionSnapshot
  ) => void;
  /** Obtiene refunds locales pendientes de confirmación (para visualización, no sincronización directa) */
  getPendingLocalRefunds: () => Refund[];
  /** Marca una venta como sincronizada después de confirmación del servidor */
  markSaleAsSynced: (offlineUuid: string, serverId: number) => void;
  /** Genera un nuevo offline_uuid para ventas creadas sin conexión */
  generateOfflineUuid: () => string;
  /** Limpia registros offline que ya fueron confirmados por el servidor */
  reconcileWithServer: (serverData: SalesWithCreditNotes) => void;

  resetStore: () => void;
}

export const useSaleStore = create<ISalesState>()(
  devtools(
    persist(
      (set, get) => ({
        taxedAmount: 0,
        setTaxedAmount: (taxedAmount: number) => set({ taxedAmount }),
        exemptedAmount: 0,
        setExemptedAmount: (exemptedAmount) => set({ exemptedAmount }),
        unaffectedAmount: 0,
        setUnaffectedAmount: (unaffectedAmount) => set({ unaffectedAmount }),
        freeAmount: 0,
        setFreeAmount: (freeAmount) => set({ freeAmount }),
        vatAmount: 0,
        setVatAmount: (vatAmount) => set({ vatAmount }),
        grossAmount: 0,
        setGrossAmount: (grossAmount: number) => set({ grossAmount }),
        totalAmount: 0,
        setTotalAmount: (totalAmount: number) => set({ totalAmount }),
        discount: 0,
        setDiscount: (discount: number) => set({ discount }),
        discountType: "",
        setDiscountType: (discountType: string) => set({ discountType }),
        creditData: undefined,
        setCreditData: (creditData) => set({ creditData }),
        paymentDates: [],
        setPaymentDates: (paymentDates: PaymentDate[]) => set({ paymentDates }),
        additionalData: DEFAULT_ADDITIONAL_DATA,
        setAdditionalData: (additionalData: IAdditionalData) =>
          set({ additionalData }),
        sales: [],
        addSale: (sale: ISale) => {
          set(({ sales }) => {
            const alreadyExists = sales.some((s) => s.uuid === sale.uuid);
            if (alreadyExists) return { sales };
            return { sales: [...sales, sale] };
          });
        },
        listSale: [],
        setListSale: (listSale: SalesResponse[]) => set({ listSale }),
        lastSaleUuid: undefined,
        setLastSaleUuid: (lastSaleUuid: string | undefined) =>
          set({ lastSaleUuid }),
        filterPaymentMethod: 0,
        setFilterPaymentMethod: (filterPaymentMethod: number) =>
          set({ filterPaymentMethod }),
        filterBillType: 0,
        setFilterBillType: (filterBillType: number) => set({ filterBillType }),
        startDate: undefined,
        setStartDate: (startDate: Date | undefined) => set({ startDate }),
        endDate: undefined,
        setEndDate: (endDate: Date | undefined) => set({ endDate }),
        offline: false,
        setOffline: (offline: boolean) => set({ offline }),
        cancelSaleItem: [],
        setCancelSaleItem: (cancelSale: CancelSale[]) =>
          set({ cancelSaleItem: cancelSale }),
        getSale: (uuid: string) => {
          const sales = get().sales;
          const foundSale = sales.find((sale) => sale.uuid === uuid);
          if (!foundSale) return undefined;

          return {
            ...foundSale,
            bill: {
              ...foundSale.bill,
              items: groupItemsByProductAndContainer(foundSale.bill.items),
            },
          };
        },
        updateSaleWithResponse: (uuid, saleResponse) => {
          const sales = get().sales;
          const updatedSales = sales.map((prevSale) =>
            prevSale.uuid === uuid
              ? {
                  ...prevSale,
                  id: saleResponse.id,
                  bill: {
                    ...prevSale.bill,
                    serial: saleResponse.serial,
                    correlative: saleResponse.correlative,
                  },
                }
              : prevSale
          );

          set(() => ({ sales: updatedSales }));
        },
        enableQuickPay: false,
        setEnableQuickPay: (enableQuickPay: boolean) => set({ enableQuickPay }),
        success: false,
        setSuccess: (success: boolean) => set({ success }),
        loading: false,
        setLoading: (loading: boolean) => set({ loading }),
        allLocalSales: {
          sales: [],
          credit_notes: [],
          refunds: [],
        },
        setAllLocalSales: (allLocalSales: SalesWithCreditNotes) =>
          set({ allLocalSales }),
        
        /**
         * Merge inteligente: combina datos del servidor con ventas locales no sincronizadas.
         * Las ventas offline sin sincronizar se preservan.
         */
        mergeWithServerData: (serverData: SalesWithCreditNotes) => {
          const current = get().allLocalSales;
          
          // Filtrar ventas locales que NO están sincronizadas (creadas offline y pendientes)
          const unsyncedLocalSales = current.sales.filter(
            (sale) => sale.synced === false && sale.offline_uuid
          );
          const unsyncedLocalRefunds = current.refunds.filter(
            (refund) => refund.synced === false && refund.offline_uuid
          );
          const unsyncedLocalCreditNotes = current.credit_notes.filter(
            (cn) => (cn as any).synced === false && (cn as any).offline_uuid
          );
          
          // Crear set de offline_uuids que vienen del servidor para evitar duplicados
          const serverOfflineUuids = new Set([
            ...serverData.sales.filter(s => s.offline_uuid).map(s => s.offline_uuid),
            ...serverData.refunds.filter(r => r.offline_uuid).map(r => r.offline_uuid),
          ]);
          
          // Solo mantener ventas locales que NO están en el servidor
          const filteredLocalSales = unsyncedLocalSales.filter(
            (sale) => !serverOfflineUuids.has(sale.offline_uuid)
          );
          const filteredLocalRefunds = unsyncedLocalRefunds.filter(
            (refund) => !serverOfflineUuids.has(refund.offline_uuid)
          );
          const filteredLocalCreditNotes = unsyncedLocalCreditNotes.filter(
            (cn) => !serverOfflineUuids.has((cn as any).offline_uuid)
          );
          
          set({
            allLocalSales: {
              sales: [...serverData.sales, ...filteredLocalSales],
              credit_notes: [...serverData.credit_notes, ...filteredLocalCreditNotes],
              refunds: [...serverData.refunds, ...filteredLocalRefunds],
            },
          });
        },
        
        addLocalSaleRecord: (record) => {
          const current = get().allLocalSales;

          if ("uuid" in record) {
            // Es una venta - agregar con synced=false si no tiene el flag
            const saleRecord = {
              ...record,
              synced: record.synced ?? false,
              offline_uuid: record.offline_uuid ?? (record.createdOffline ? uuidv4() : null),
            };

            // Verificar si ya existe una venta con el mismo uuid o offline_uuid
            const alreadyExists = current.sales.some(
              (s) =>
                (saleRecord.offline_uuid && s.offline_uuid === saleRecord.offline_uuid) ||
                (saleRecord.uuid && s.uuid === saleRecord.uuid)
            );

            if (alreadyExists) {
              // Ya existe, actualizar en lugar de duplicar
              const updatedSales = current.sales.map((s) =>
                (saleRecord.offline_uuid && s.offline_uuid === saleRecord.offline_uuid) ||
                (saleRecord.uuid && s.uuid === saleRecord.uuid)
                  ? { ...s, ...saleRecord }
                  : s
              );
              set({
                allLocalSales: {
                  ...current,
                  sales: updatedSales,
                },
              });
            } else {
              set({
                allLocalSales: {
                  ...current,
                  sales: [...current.sales, saleRecord],
                },
              });
            }
          } else {
            // Es una nota de crédito
            set({
              allLocalSales: {
                ...current,
                credit_notes: [...current.credit_notes, record],
              },
            });
          }
        },

        /**
         * Agrega un refund local para visualización temporal.
         * NOTA: Los refunds NO se sincronizan directamente al servidor.
         * El servidor genera refunds automáticamente cuando procesa una anulación (cancelSale).
         * Este método es para mostrar el refund en la UI mientras el usuario está offline.
         */
        addLocalRefund: (refund: Refund) => {
          const current = get().allLocalSales;
          // Marcar como no sincronizado (local temporal)
          const refundRecord = {
            ...refund,
            synced: false, // Siempre false porque es local
            offline_uuid: refund.offline_uuid ?? uuidv4(),
          };

          // Verificar si ya existe un refund con el mismo offline_uuid
          const alreadyExists = current.refunds.some(
            (r) => refundRecord.offline_uuid && r.offline_uuid === refundRecord.offline_uuid
          );
          if (alreadyExists) return;

          set({
            allLocalSales: {
              ...current,
              refunds: [...current.refunds, refundRecord],
            },
          });
        },

        /** Obtiene ventas que aún no han sido sincronizadas con el servidor */
        getUnsyncedSales: () => {
          const { allLocalSales } = get();
          const unsynced = allLocalSales.sales.filter(
            (sale) => sale.synced === false && sale.offline_uuid
          );
          // Deduplicar por offline_uuid como red de seguridad
          const seen = new Set<string>();
          return unsynced.filter((sale) => {
            if (!sale.offline_uuid || seen.has(sale.offline_uuid)) return false;
            seen.add(sale.offline_uuid);
            return true;
          });
        },

        getUnsyncedSalesForAutoSync: () => {
          return get()
            .getUnsyncedSales()
            .filter((sale) => !isStoreSaleSyncExhausted(sale));
        },

        recordStoreSaleSyncFailure: (offlineUuid: string, errorMessage: string) => {
          const current = get().allLocalSales;
          const updatedSales = current.sales.map((sale) => {
            if (sale.offline_uuid !== offlineUuid) return sale;

            const attempts = (sale.sync_attempts ?? 0) + 1;
            const exhausted = attempts >= MAX_OFFLINE_SYNC_ATTEMPTS;

            return {
              ...sale,
              sync_attempts: attempts,
              last_sync_error: errorMessage,
              sync_auto_retry_exhausted: exhausted,
            };
          });

          set({
            allLocalSales: {
              ...current,
              sales: updatedSales,
            },
          });
        },

        markStoreSaleSyncBlocked: (offlineUuid: string, errorMessage: string) => {
          const current = get().allLocalSales;
          const updatedSales = current.sales.map((sale) => {
            if (sale.offline_uuid !== offlineUuid) return sale;

            return {
              ...sale,
              sync_attempts: MAX_OFFLINE_SYNC_ATTEMPTS,
              last_sync_error: errorMessage,
              sync_auto_retry_exhausted: true,
            };
          });

          set({
            allLocalSales: {
              ...current,
              sales: updatedSales,
            },
          });
        },

        resetStoreSaleSyncAttempts: (offlineUuid: string) => {
          const current = get().allLocalSales;
          const updatedSales = current.sales.map((sale) => {
            if (sale.offline_uuid !== offlineUuid) return sale;

            return {
              ...sale,
              sync_attempts: 0,
              last_sync_error: null,
              sync_auto_retry_exhausted: false,
            };
          });

          set({
            allLocalSales: {
              ...current,
              sales: updatedSales,
            },
          });
        },

        updateOfflineSaleOpenSession: (
          offlineUuid: string,
          session: OpenSaleSessionSnapshot
        ) => {
          const current = get().allLocalSales;
          const updatedSales = current.sales.map((sale) => {
            if (sale.offline_uuid !== offlineUuid) return sale;

            return {
              ...sale,
              cashier: {
                id: session.cashier.id,
                name: session.cashier.name,
              },
              workshift: {
                id: session.workshift.id,
                name: session.workshift.name,
              },
              seller: {
                id: session.seller.id,
                name: session.seller.name,
                lastname: session.seller.last_name,
              },
            };
          });

          set({
            allLocalSales: {
              ...current,
              sales: updatedSales,
            },
          });
        },

        /** 
         * Obtiene refunds locales pendientes de confirmación.
         * NOTA: Los refunds NO se sincronizan directamente - el servidor los genera
         * automáticamente cuando se procesa una anulación (cancelSale).
         * Esta función es solo para visualización local.
         */
        getPendingLocalRefunds: () => {
          const { allLocalSales } = get();
          // Refunds locales que aún no aparecen en el servidor
          return allLocalSales.refunds.filter(
            (refund) => refund.synced === false
          );
        },

        /** Marca una venta como sincronizada después de confirmación del servidor */
        markSaleAsSynced: (offlineUuid: string, serverId: number) => {
          const current = get().allLocalSales;
          let merged = false;
          const updatedSales = current.sales.flatMap((sale) => {
            if (sale.offline_uuid !== offlineUuid) {
              return [sale];
            }
            if (merged) {
              return [];
            }
            merged = true;
            return [
              {
                ...sale,
                synced: true,
                id: serverId,
                sync_attempts: 0,
                last_sync_error: null,
                sync_auto_retry_exhausted: false,
              },
            ];
          });

          set({
            allLocalSales: {
              ...current,
              sales: updatedSales,
            },
          });
        },

        /** Genera un nuevo offline_uuid para ventas creadas sin conexión */
        generateOfflineUuid: () => uuidv4(),

        /** 
         * Limpia registros offline que ya fueron confirmados por el servidor.
         * 
         * Para VENTAS: Compara offline_uuid, elimina locales confirmados, mantiene pendientes.
         * Para REFUNDS: El servidor es la fuente de verdad (se generan al procesar anulaciones),
         *               solo mantenemos refunds locales para visualización temporal hasta que
         *               la anulación sea procesada.
         */
        reconcileWithServer: (serverData: SalesWithCreditNotes) => {
          const current = get().allLocalSales;
          
          // Crear set de offline_uuids de ventas que vienen del servidor
          const serverSaleOfflineUuids = new Set<string>();
          serverData.sales.forEach((s) => {
            if (s.offline_uuid) serverSaleOfflineUuids.add(s.offline_uuid);
          });
          
          // Eliminar ventas locales que ya están confirmadas en el servidor
          const reconciledSales = current.sales.filter((sale) => {
            if (!sale.offline_uuid) return true; // Mantener ventas sin offline_uuid
            if (serverSaleOfflineUuids.has(sale.offline_uuid)) {
              return false; // Ya está en el servidor, eliminar local
            }
            return true; // Mantener, aún no confirmada
          });
          
          // Para refunds: el servidor es la fuente de verdad.
          // Los refunds locales son solo para visualización temporal.
          // Cuando el servidor procesa una anulación, genera el refund real.
          // Filtramos refunds locales que ya tienen equivalente en el servidor (por sale_id)
          const serverRefundSaleIds = new Set(serverData.refunds.map(r => r.sale_id));
          const pendingLocalRefunds = current.refunds.filter((localRefund) => {
            // Si el servidor ya tiene un refund para esta venta, eliminar el local
            if (serverRefundSaleIds.has(localRefund.sale_id)) {
              console.log(`[Reconcile] Eliminando refund local, servidor ya lo procesó: sale_id=${localRefund.sale_id}`);
              return false;
            }
            // Mantener refunds locales para ventas que aún no fueron procesadas
            return localRefund.synced === false;
          });
          
          // Combinar: servidor + locales no confirmados
          set({
            allLocalSales: {
              sales: [...serverData.sales, ...reconciledSales.filter(s => s.synced === false)],
              credit_notes: [...serverData.credit_notes],
              // Refunds: servidor + locales pendientes (para visualización temporal)
              refunds: [...serverData.refunds, ...pendingLocalRefunds],
            },
          });
        },

        resetStore: () =>
          set({
            taxedAmount: 0,
            exemptedAmount: 0,
            unaffectedAmount: 0,
            freeAmount: 0,
            vatAmount: 0,
            grossAmount: 0,
            totalAmount: 0,
            discount: 0,
            discountType: "",
            creditData: undefined,
            paymentDates: [],
            additionalData: DEFAULT_ADDITIONAL_DATA,
            sales: [],
            listSale: [],
            lastSaleUuid: undefined,
            filterPaymentMethod: 0,
            filterBillType: 0,
            startDate: undefined,
            endDate: undefined,
            offline: false,
            cancelSaleItem: [],
            enableQuickPay: false,
            success: false,
            loading: false,
            allLocalSales: { sales: [], credit_notes: [], refunds: [] },
          }),
      }),
      {
        name: "sale-store",
        partialize: (state) => ({
          taxedAmount: state.taxedAmount,
          exemptedAmount: state.exemptedAmount,
          unaffectedAmount: state.unaffectedAmount,
          freeAmount: state.freeAmount,
          vatAmount: state.vatAmount,
          grossAmount: state.grossAmount,
          totalAmount: state.totalAmount,
          discount: state.discount,
          discountType: state.discountType,
          creditData: state.creditData,
          paymentDates: state.paymentDates,
          additionalData: state.additionalData,
          listSale: state.listSale,
          lastSaleUuid: state.lastSaleUuid,
          sales: state.sales,
          filterPaymentMethod: state.filterPaymentMethod,
          filterBillType: state.filterBillType,
          startDate: state.startDate,
          endDate: state.endDate,
          cancelSaleItem: state.cancelSaleItem,
          offline: state.offline,
          enableQuickPay: state.enableQuickPay,
          allLocalSales: state.allLocalSales,
        }),
        storage: createJSONStorage(() => IdbStorage),
      }
    )
  )
);
